import React from 'react';
import { Box, Typography } from '@mui/material';
import { Lock as LockIcon, LockOpen as LockOpenIcon } from '@mui/icons-material';

interface RouteNameOverlayProps {
  routeName: string | null;
  hasUnsavedChanges?: boolean;
  hasRouteInProgress?: boolean; // New prop to indicate if route planning is in progress
  isPublic?: boolean; // Route visibility setting
}

export const RouteNameOverlay: React.FC<RouteNameOverlayProps> = React.memo(({ 
  routeName, 
  hasUnsavedChanges = false,
  hasRouteInProgress = false,
  isPublic = false
}) => {
  // Don't render anything if there's no route name and no route in progress
  if (!routeName && !hasRouteInProgress) {
    return null;
  }

  // Determine what to display
  const displayName = routeName || 'unnamed Route';
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 8,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.9)', // Less transparent (was 0.7)
        color: '#333',
        padding: '6px 10px', // Slightly more padding for better centering
        borderRadius: '4px',
        maxWidth: 'calc(100% - 16px)',
        textAlign: 'left',
        pointerEvents: 'none',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center', // Center content vertically
        gap: 1, // Add spacing between text and icon
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '10px',
          fontWeight: 400,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.2,
        }}
      >
        <Box component="span" sx={{ fontWeight: 600 }}>
          Route:
        </Box>{' '}
        {displayName}
        {(hasUnsavedChanges || !routeName) && (
          <Box 
            component="span" 
            sx={{ 
              color: '#f57c00', // Orange color
              fontWeight: 500,
              marginLeft: '4px'
            }}
          >
            (not saved)
          </Box>
        )}
      </Typography>
      
      {/* Show visibility icon only for saved routes (not for unsaved/in-progress routes) */}
      {routeName && !hasUnsavedChanges && (
        isPublic ? (
          <LockOpenIcon 
            sx={{ 
              fontSize: '12px', 
              color: '#4caf50', // Green for public
              opacity: 0.8
            }} 
          />
        ) : (
          <LockIcon 
            sx={{ 
              fontSize: '12px', 
              color: '#666', // Gray for private
              opacity: 0.6
            }} 
          />
        )
      )}
    </Box>
  );
});

RouteNameOverlay.displayName = 'RouteNameOverlay';
